import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { apiClient } from '../../lib/api-client';
import { User } from '../../lib/postgresql-types';
import { postgresqlAuthService } from '../../lib/auth-service';
import { safeStorage } from '../../lib/safe-storage';
import { toast } from 'sonner';

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error?: string; user?: User }>;
  signUp: (data: {
    email: string;
    password: string;
    full_name: string;
    user_type: 'job_seeker' | 'employer';
    phone_number?: string;
    company_data?: {
      name: string;
      description: string;
      website?: string;
      location: string;
      industry: string;
      size: string;
      founded_year?: number;
      employer_job_title?: string;
      employer_department?: string;
    };
  }) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>; // Alias for compatibility
  verifyOTP: (otp: string) => Promise<{ error?: string; user?: User }>;
  resendOTP: () => Promise<{ error?: string }>;
  forgotPassword: (email: string) => Promise<{ error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ error?: string }>;
  changePassword: (currentPassword: string, newPassword: string, otp?: string) => Promise<{ error?: string }>;
  sendAdminPasswordOTP: () => Promise<{ error?: string }>;
  updateProfile: (updates: Partial<User>) => Promise<{ error?: string }>;
  uploadProfileImage: (file: File) => Promise<{ error?: string; imageUrl?: string }>;
  uploadResume: (file: File) => Promise<{ error?: string; resumeUrl?: string; fileName?: string }>;
  isAuthenticated: boolean;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  const logoutTimerRef = useRef<number | null>(null);

  const scheduleAutoLogoutFromToken = (token: string) => {
    try {
      const parts = String(token).split('.');
      if (parts.length >= 2) {
        const payloadJson = atob(parts[1]);
        const payload = JSON.parse(payloadJson || '{}');
        const exp = Number(payload?.exp || 0);
        const msUntilExpiry = exp > 0 ? (exp * 1000) - Date.now() : 0;
        if (logoutTimerRef.current) {
          try { window.clearTimeout(logoutTimerRef.current); } catch { }
          logoutTimerRef.current = null;
        }
        if (msUntilExpiry <= 0) {
          toast.info('Session expired. Please log in again.');
          // Ensure sign out clears token and user
          (async () => { await signOut(); })();
        } else {
          const id = window.setTimeout(async () => {
            toast.info('Session expired. Please log in again.');
            await signOut();
          }, msUntilExpiry);
          logoutTimerRef.current = id as unknown as number;
        }
      }
    } catch {
      // If token cannot be decoded, fallback to 5 minutes absolute timeout
      if (logoutTimerRef.current) {
        try { window.clearTimeout(logoutTimerRef.current); } catch { }
        logoutTimerRef.current = null;
      }
      const id = window.setTimeout(async () => {
        toast.info('Session expired. Please log in again.');
        await signOut();
      }, 5 * 60 * 1000);
      logoutTimerRef.current = id as unknown as number;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔄 AuthContext - Initializing auth...');
        const token = safeStorage.getItem('megajobnepal_auth_token');
        console.log('🔍 AuthContext - Token found:', !!token);

        if (token) {
          console.log('🔄 AuthContext - Getting profile...');
          const profileResponse = await apiClient.getProfile();
          console.log('🔍 AuthContext - Profile response:', profileResponse);

          if (profileResponse && profileResponse.user) {
            console.log('🔍 AuthContext - Setting user:', profileResponse.user);
            console.log('🔍 AuthContext - User type:', profileResponse.user.user_type);
            setUser(profileResponse.user);
            scheduleAutoLogoutFromToken(token);
          } else {
            console.log('⚠️ AuthContext - No profile data, attempting token decode for minimal user');
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                const minimalUser: any = {
                  id: payload?.userId || '',
                  _id: payload?.userId || '',
                  email: payload?.email || '',
                  user_type: payload?.user_type || '',
                  full_name: payload?.email || ''
                };
                if (minimalUser.user_type) {
                  setUser(minimalUser);
                  scheduleAutoLogoutFromToken(token);
                } else {
                  console.log('⚠️ Token payload missing user_type; retaining token without user');
                }
              }
            } catch {
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        console.log('✅ AuthContext - Initialization complete');
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const signIn = async (email: string, password: string, captchaToken?: string): Promise<{ error?: string; user?: User }> => {
    console.log('🚀 AuthContext - signIn function called!');
    console.log('🔍 AuthContext - Email:', email);
    console.log('🔍 AuthContext - Password length:', password?.length);

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 AuthContext - Signing in...');
      const currentPath = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
      const isAdminRoute = currentPath === '/super-admin/login' || currentPath.startsWith('/super-admin');
      const result = isAdminRoute
        ? await apiClient.adminLogin({ email, password, captchaToken })
        : await apiClient.login({ email, password, captchaToken });
      console.log('🔍 AuthContext - Login result:', result);

      if (!result.token) {
        console.log('⚠️ AuthContext - No token in response');
        setError(result.error || 'Login failed');
        return { error: result.error || 'Login failed' };
      }

      console.log('🔍 AuthContext - User data from login:', result.user);
      console.log('🔍 AuthContext - User type from login:', result.user?.user_type);

      const isSuperAdmin = String(result?.user?.user_type || '').toLowerCase() === 'super_admin';

      // Store token and refresh full profile for consistent fields
      safeStorage.setItem('megajobnepal_auth_token', result.token);
      scheduleAutoLogoutFromToken(result.token);
      try {
        localStorage.removeItem('mysql_megajob_db_users');
        localStorage.removeItem('postgresql_megajob_db_users');
        sessionStorage.removeItem('mysql_megajob_db_users');
      } catch { }
      try {
        const profileResponse = await apiClient.getProfile();
        if (profileResponse && profileResponse.user) {
          setUser(profileResponse.user);
        } else {
          setUser(result.user);
        }
      } catch {
        setUser(result.user);
      }

      return { user: result.user };
    } catch (error) {
      const status = (error as any)?.status;
      let errorMessage = 'Sign in failed';
      if (status === 401) {
        errorMessage = 'Invalid email or password.';
      } else if (status === 404) {
        errorMessage = 'Account not found.';
      } else if (status === 403) {
        errorMessage = 'Access denied. Please contact an administrator to request access.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      console.error('❌ AuthContext - Sign in error:', error);
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (userData: {
    email: string;
    password: string;
    full_name: string;
    user_type: 'job_seeker' | 'employer';
    phone_number?: string;
    company_data?: {
      name: string;
      description: string;
      website?: string;
      location: string;
      industry: string;
      size: string;
      founded_year?: number;
      employer_job_title?: string;
      employer_department?: string;
    };
  }): Promise<{ error?: string }> => {
    try {
      setLoading(true);
      setError(null);

      try {
        const existingToken = safeStorage.getItem('megajobnepal_auth_token');
        if (existingToken || user) {
          await signOut();
        }
      } catch { }

      // First, send OTP to email
      const otpResult = await apiClient.sendOTP(userData.email);
      console.log('🔍 OTP Send Result:', otpResult);

      if (!otpResult || !otpResult.message) {
        const errorMsg = otpResult?.error || 'Failed to send OTP email. Please check your console.';
        toast.error(errorMsg);
        return { error: errorMsg };
      }

      // Store email and user data for OTP verification (we'll register after OTP verification)
      setPendingUserId(userData.email);
      setPendingUserData({
        email: userData.email,
        password: userData.password,
        fullName: userData.full_name,
        userType: userData.user_type,
        phone: userData.phone_number,
        companyData: userData.company_data
      });

      return {};
    } catch (error) {
      let errorMessage = 'Sign up failed';
      const status = (error as any)?.status;
      const msg = error instanceof Error ? error.message : '';
      if (error instanceof Error) {
        if (msg.includes('An account with this email already exists')) {
          errorMessage = msg;
        } else if (msg.includes('User already exists') || msg.includes('409')) {
          errorMessage = 'An account with this email already exists. Please try logging in instead.';
        } else if (status === 404 || /not found/i.test(msg)) {
          const emailVal = String(userData?.email || '').trim().toLowerCase();
          const domain = emailVal.split('@')[1] || '';
          if (domain === 'gmail.com') {
            const normalizeGmailLocal = (s: string) => String(s || '').toLowerCase().split('+')[0].replace(/\./g, '');
            const fullName = String(userData?.full_name || '').trim();
            const parts = fullName.split(/\s+/);
            const first = parts[0] || '';
            const last = parts.slice(1).join(' ') || '';
            const f = String(first || '').trim().toLowerCase().replace(/[^a-z]/g, '');
            const l = String(last || '').trim().toLowerCase().replace(/[^a-z]/g, '');
            const expectedLocalDots = f && l ? `${f}.${l}` : (f || l);
            if (expectedLocalDots) {
              errorMessage = `Email not found. Please check and enter your Gmail address. Did you mean ${expectedLocalDots}@gmail.com?`;
            } else {
              errorMessage = 'Email not found. Please check and enter a proper Gmail address (e.g., example@gmail.com).';
            }
          } else {
            errorMessage = 'Email not found. Please check and enter a proper Gmail address (e.g., example@gmail.com).';
          }
        } else {
          errorMessage = msg;
        }
      }
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (otp: string): Promise<{ error?: string; user?: User }> => {
    try {
      setLoading(true);
      setError(null);

      if (!pendingUserId || !pendingUserData) {
        const errorMsg = 'No pending verification found';
        toast.error(errorMsg);
        return { error: errorMsg };
      }

      // Complete the signup with OTP verification
      const registerResult = await apiClient.completeSignup({
        email: pendingUserData.email,
        password: pendingUserData.password,
        user_type: pendingUserData.userType,
        full_name: pendingUserData.fullName,
        otp: otp,
        phone_number: pendingUserData.phone,
        company_data: pendingUserData.companyData
          ? {
            // Map frontend keys to backend expected schema
            company_name: pendingUserData.companyData.name,
            company_description: pendingUserData.companyData.description,
            company_website: pendingUserData.companyData.website,
            company_location: pendingUserData.companyData.location,
            company_industry: pendingUserData.companyData.industry,
            company_size: pendingUserData.companyData.size,
            company_founded_year: pendingUserData.companyData.founded_year,
            employer_job_title: pendingUserData.companyData.employer_job_title,
            employer_department: pendingUserData.companyData.employer_department,
          }
          : undefined,
      });

      if (registerResult.token) {
        // Auto-login the user after successful signup verification
        safeStorage.setItem('megajobnepal_auth_token', registerResult.token);
        scheduleAutoLogoutFromToken(registerResult.token);

        // Clear old cached data
        try {
          localStorage.removeItem('mysql_megajob_db_users');
          localStorage.removeItem('postgresql_megajob_db_users');
          sessionStorage.removeItem('mysql_megajob_db_users');
        } catch { }

        // Fetch and set user profile
        try {
          const profileResponse = await apiClient.getProfile();
          if (profileResponse && profileResponse.user) {
            setUser(profileResponse.user);
            setPendingUserId(null);
            setPendingUserData(null);
            toast.success('Account created successfully! Welcome!');
            try {
              const t = String((profileResponse.user as any)?.user_type || '').toLowerCase();
              if (t === 'job_seeker') {
                window.location.href = '/jobseeker-dashboard';
              } else if (t === 'employer') {
                window.location.href = '/employer/dashboard';
              } else if (t === 'admin') {
                window.location.href = '/admin/dashboard';
              } else if (t === 'super_admin') {
                window.location.href = '/super-admin/dashboard';
              }
            } catch { }
            return { user: profileResponse.user };
          }
        } catch { }

        // Fallback: use user data from registration result
        if (registerResult.user) {
          setUser(registerResult.user);
          setPendingUserId(null);
          setPendingUserData(null);
          toast.success('Account created successfully! Welcome!');
          try {
            const t = String((registerResult.user as any)?.user_type || '').toLowerCase();
            if (t === 'job_seeker') {
              window.location.href = '/jobseeker-dashboard';
            } else if (t === 'employer') {
              window.location.href = '/employer/dashboard';
            } else if (t === 'admin') {
              window.location.href = '/admin/dashboard';
            } else if (t === 'super_admin') {
              window.location.href = '/super-admin/dashboard';
            }
          } catch { }
          return { user: registerResult.user };
        }

        // Fallback: synthesize minimal user when token exists but user object is unavailable
        const fallbackUser: any = {
          id: '',
          email: pendingUserData.email,
          user_type: pendingUserData.userType,
          full_name: pendingUserData.fullName,
          is_verified: true,
        };
        setUser(fallbackUser);
        setPendingUserId(null);
        setPendingUserData(null);
        toast.success('Account created successfully! Welcome!');
        try {
          const t = String(fallbackUser.user_type || '').toLowerCase();
          if (t === 'job_seeker') {
            window.location.href = '/jobseeker-dashboard';
          } else if (t === 'employer') {
            window.location.href = '/employer/dashboard';
          } else if (t === 'admin') {
            window.location.href = '/admin/dashboard';
          } else if (t === 'super_admin') {
            window.location.href = '/super-admin/dashboard';
          } else {
            window.location.href = '/';
          }
        } catch { }
        return { user: fallbackUser };
      }

      return {};
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OTP verification failed';
      toast.error(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async (): Promise<{ error?: string }> => {
    try {
      setError(null);

      if (!pendingUserId) {
        const errorMsg = 'No pending verification found';
        toast.error(errorMsg);
        return { error: errorMsg };
      }

      // Resend OTP by calling sendOTP again
      const result = await apiClient.sendOTP(pendingUserId);

      if (!result.message) {
        const errorMsg = result.error || 'Failed to resend OTP';
        toast.error(errorMsg);
        return { error: errorMsg };
      }

      return {};
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend OTP';
      toast.error(errorMessage);
      return { error: errorMessage };
    }
  };

  const forgotPassword = async (email: string): Promise<{ error?: string }> => {
    try {
      setError(null);
      const result = await apiClient.forgotPassword(email);
      if (result?.resetToken || result?.message) {
        return {};
      }
      setError('Failed to send reset email');
      return { error: 'Failed to send reset email' };
    } catch (error) {
      const status = (error as any)?.status;
      let errorMessage = 'Failed to send reset email';
      if (status === 404 && error instanceof Error) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<{ error?: string }> => {
    try {
      setLoading(true);
      setError(null);

      const result = await postgresqlAuthService.resetPassword(token, newPassword);

      if (!result.success) {
        setError(result.error || 'Failed to reset password');
        return { error: result.error || 'Failed to reset password' };
      }

      return {};
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const sendAdminPasswordOTP = async (): Promise<{ error?: string }> => {
    try {
      setError(null);
      if (!user) {
        setError('Not authenticated');
        return { error: 'Not authenticated' };
      }
      if (user.user_type !== 'admin') {
        setError('Not authorized');
        return { error: 'Not authorized' };
      }
      const result = await apiClient.adminSendPasswordOTP(user.email || '');
      if (result?.message) {
        toast.success('OTP sent to admin email');
        return {};
      } else {
        const msg = result?.error || 'Failed to send OTP';
        setError(msg);
        toast.error(msg);
        return { error: msg };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
      setError(errorMessage);
      toast.error(errorMessage);
      return { error: errorMessage };
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string, otp?: string): Promise<{ error?: string }> => {
    try {
      setError(null);

      if (!user) {
        setError('Not authenticated');
        return { error: 'Not authenticated' };
      }

      if (user.user_type === 'admin') {
        const result = await apiClient.adminChangePassword({ currentPassword, newPassword });
        if (result?.message || result?.success) {
          toast.success('Password changed successfully');
          return {};
        } else {
          const msg = result?.error || 'Failed to change password';
          setError(msg);
          return { error: msg };
        }
      }

      if (!user.id) {
        setError('Not authenticated');
        return { error: 'Not authenticated' };
      }

      const result = await postgresqlAuthService.changePassword(user.id, currentPassword, newPassword);

      if (result.success) {
        toast.success('Password changed successfully');
        return {};
      } else {
        setError(result.error || 'Failed to change password');
        return { error: result.error || 'Failed to change password' };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      setError(errorMessage);
      toast.error(errorMessage);
      return { error: errorMessage };
    }
  };

  const updateProfile = async (updates: Partial<User>): Promise<{ error?: string }> => {
    try {
      setError(null);

      if (!user?._id) {
        setError('Not authenticated');
        return { error: 'Not authenticated' };
      }

      const response = await apiClient.updateProfile(updates);

      if (response && response.user) {
        setUser(response.user);
        return {};
      }
      setError('Failed to update profile');
      return { error: 'Failed to update profile' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
      setError(errorMessage);
      toast.error(errorMessage);
      return { error: errorMessage };
    }
  };

  const uploadProfileImage = async (file: File): Promise<{ error?: string; imageUrl?: string }> => {
    try {
      setError(null);

      if (!user?._id) {
        setError('Not authenticated');
        return { error: 'Not authenticated' };
      }

      const response = await apiClient.uploadProfileImage(file);

      if (response && response.imageUrl) {
        // Refresh user from backend to ensure consistent shape and fields
        try {
          if (response.user) {
            setUser(response.user);
          } else {
            const refreshed = await apiClient.getProfile();
            if (refreshed && refreshed.user) {
              setUser(refreshed.user);
            } else {
              // Fallback: update just the field locally
              setUser(prev => prev ? { ...prev, profileImage: response.imageUrl } : null);
            }
          }
        } catch {
          // Fallback: update just the field locally
          setUser(prev => prev ? { ...prev, profileImage: response.imageUrl } : null);
        }
        return { imageUrl: response.imageUrl };
      }
      setError('Failed to upload profile image');
      return { error: 'Failed to upload profile image' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload profile image';
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  const uploadResume = async (file: File): Promise<{ error?: string; resumeUrl?: string; fileName?: string }> => {
    try {
      setError(null);

      if (!user?._id) {
        setError('Not authenticated');
        return { error: 'Not authenticated' };
      }

      const response = await apiClient.uploadResume(file);

      if (response && response.resumeUrl) {
        // Refresh user from backend to ensure consistent shape and fields
        try {
          const refreshed = await apiClient.getProfile();
          if (refreshed && refreshed.user) {
            setUser(refreshed.user);
          } else {
            // Fallback: update just the field locally
            setUser(prev => prev ? { ...prev, resume: response.resumeUrl, resumeFileName: response.fileName } : null);
          }
        } catch {
          setUser(prev => prev ? { ...prev, resume: response.resumeUrl, resumeFileName: response.fileName } : null);
        }
        return { resumeUrl: response.resumeUrl, fileName: response.fileName };
      }
      setError('Failed to upload resume');
      return { error: 'Failed to upload resume' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload resume';
      setError(errorMessage);
      return { error: errorMessage };
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      safeStorage.removeItem('megajobnepal_auth_token');
      setUser(null);
      setPendingUserId(null);
      setPendingUserData(null);
      setError(null);
      if (logoutTimerRef.current) {
        try { window.clearTimeout(logoutTimerRef.current); } catch { }
        logoutTimerRef.current = null;
      }
      try {
        localStorage.removeItem('mysql_megajob_db_users');
        localStorage.removeItem('postgresql_megajob_db_users');
        sessionStorage.removeItem('mysql_megajob_db_users');
      } catch { }
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!safeStorage.getItem('megajobnepal_auth_token'),
    error,
    signIn,
    signUp,
    signOut,
    logout: signOut, // Alias for compatibility
    verifyOTP,
    resendOTP,
    forgotPassword,
    resetPassword,
    changePassword,
    sendAdminPasswordOTP,
    updateProfile,
    uploadProfileImage,
    uploadResume,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
